<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid, reset, errors}" ref="observer">
    <artisio-modal size="lg" :visible="modal.show" :data="model" scrollable
                   :title="computedTitle" @show="onShow"
                   :disable-submit-btn="!model.invoices || model.invoices.length === 0"
                   @hidden="onHidden" @ok.prevent="handleSubmit(onSubmit)" :show-confirmation="showConfirmation"
                   :ok-title="$t('Submit')" :is-loading="loading" modal-class="mobile-full-screen">


      <div class="row" v-if="sourceInvoice">
        <div class="col-md-6">
          <div>
            <b>{{ $t('Invoice') }}:</b>
            {{ sourceInvoice.invoice_no }}
          </div>
          <div>
            <b>{{ $t('Total Amount') }}:</b>
            {{ sourceInvoice.total_amount | asCurrency(sourceInvoice.currency.symbol) }}
          </div>
          <div>
            <b>{{ $t('Paid Amount') }}:</b>
            {{ sourceInvoice.paid_amount | asCurrency(sourceInvoice.currency.symbol) }}
          </div>
          <div>
            <b>{{ $t('Overpaid Amount') }}:</b>
            {{ sourceInvoice.outstanding_amount * -1 | asCurrency(sourceInvoice.currency.symbol) }}
          </div>
        </div>
        <div class="col-md-6">
          <customer-email-address-info :customer="sourceInvoice.customer"></customer-email-address-info>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <hr>
        </div>
      </div>

      <div class="row">
        <div class="col-md-4">
          <input-widget :model="model" attribute="date" type="datepicker"/>
        </div>
        <div class="col-md-4">
          <input-widget :model="model" attribute="method" disabled/>
        </div>
        <div class="col-md-4" v-if="paymentBankAccounts && paymentBankAccounts.length > 0">
          <input-widget :model="model" attribute="bank_account" value-field="value"
                        display-field="${item.key}" type="select2" :items="paymentBankAccounts"/>
        </div>
      </div>

      <div class="row mt-3 mb-2">
        <div class="col-md-12 d-flex justify-content-between">
          <h5 class="mb-0">{{ $t('Target Invoices') }}</h5>
          <b-button size="sm" variant="primary" @click="onAddInvoiceClick()">
            <font-awesome-icon icon="plus"/>
            {{ $t('Add Invoice') }}
          </b-button>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <table class="info-table table table-sm lots-table">
            <tr>
              <th style="width: 40%">{{ $t('Invoice') }}</th>
              <th style="width: 25%">{{ $t('Outstanding Amount') }}</th>
              <th style="width: 25%">{{ $t('Amount') }}</th>
              <th style="width: 5%">{{ $t('Actions') }}</th>
            </tr>
            <tr v-if="model.invoices.length === 0">
              <td colspan="4">
                <no-data-available height="4"/>
              </td>
            </tr>
            <template v-else>
              <tr v-for="invoiceItem in model.invoices" :key="invoiceItem.uuid">
                <td>
                  <invoice-select-input-widget :model="invoiceItem" attribute="invoice_uuid" :label="false"
                                               :options="{'placeholder': $t('Invoice')}"
                                               @select="onInvoiceSelect($event, invoiceItem)"
                                               @unselect="onInvoiceUnselect(invoiceItem)"
                                               :extra-filters="{customer_uuid: sourceInvoice.customer_uuid, expand: 'total_amount,paid_amount,outstanding_amount'}"/>
                </td>
                <td>
                  <currency-input-widget :model="invoiceItem" attribute="outstanding_amount" :label="false"
                                         :placeholder="$t('Outstanding')" disabled
                                         :currency="sourceInvoice.currency.symbol"/>
                </td>
                <td>
                  <currency-input-widget :model="invoiceItem" attribute="amount" :label="false"
                                         :placeholder="$t('Amount')"
                                         :currency="sourceInvoice.currency.symbol"/>
                </td>
                <td class="text-center">
                  <b-button class="mt-1" variant="danger" size="sm" @click="onInvoiceItemRemoveClick(invoiceItem)">
                    <font-awesome-icon icon="times" class="icon"/>
                  </b-button>
                </td>
              </tr>
            </template>
          </table>
        </div>
      </div>

    </artisio-modal>
  </ValidationObserver>
</template>

<script>

  import ArtisioModal from "../../../core/components/artisio-modal/ArtisioModal";
  import {createNamespacedHelpers} from "vuex";
  import AllocatePaymentModel from "@/modules/invoices/allocate-payment/AllocatePayment.model";
  import CustomerEmailAddressInfo from "@/core/components/customer-info/CustomerEmailAddressInfo";
  import NoDataAvailable from "@/core/components/common/NoDataAvailable";
  import AllocatePaymentItemModel from "@/modules/invoices/allocate-payment/AllocatePaymentItem.model";
  import {uuid} from "vue-uuid";
  import InvoiceSelectInputWidget from "@/core/widgets/InvoiceSelectInputWidget";
  import CurrencyInputWidget from "@/core/widgets/CurrencyInputWidget";
  import InputWidget from "@/core/components/input-widget/InputWidget";

  const {mapState, mapActions} = createNamespacedHelpers('invoices');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "AllocatePaymentModal",
    components: {
      InputWidget,
      CurrencyInputWidget,
      InvoiceSelectInputWidget,
      NoDataAvailable,
      CustomerEmailAddressInfo,
      ArtisioModal
    },
    data() {
      return {
        showConfirmation: true,
        model: new AllocatePaymentModel(),
      }
    },
    computed: {
      ...mapState({
        modal: state => state.allocatePaymentModal,
        sourceInvoice: state => state.allocatePaymentModal.sourceInvoice,
        loading: state => state.allocatePaymentModal.loading,
      }),
      ...globalMapState({
        paymentBankAccounts: state => state.paymentBankAccounts,
      }),
      computedTitle() {
        if (!this.sourceInvoice) {
          return this.$i18n.t('Allocate Payment')
        }
        return this.$i18n.t('Allocate Payment for {no}', {no: this.sourceInvoice.invoice_no})
      },
    },
    watch: {},
    methods: {
      ...mapActions(['hideAllocatePaymentModal', 'submitAllocatePayment', 'getTableData']),
      async onShow() {
        this.showConfirmation = true;
      },
      onHidden() {
        this.model = new AllocatePaymentModel();
        this.hideAllocatePaymentModal();
      },
      async onSubmit() {
        let totalAmount = 0
        let overpaymentAmount = this.sourceInvoice.outstanding_amount * -1
        let invoiceUuids = {}
        let duplicatedInvoiceNumbers = {}
        let creditedInvoiceNos = {}
        let cancelledInvoiceNos = {}
        let paidInvoiceNos = {}
        let exceedingTargetAmountInvoiceNos = {}

        for (let invoiceItem of this.model.invoices) {
          if (invoiceItem.invoice.currency_uuid !== this.sourceInvoice.currency.uuid) {
            this.$errorDialog(this.$i18n.t('Overpayment allocation is restricted to invoices in different currencies. Please adjust the allocation.'))
            return false
          }

          if (invoiceUuids[invoiceItem.invoice_uuid]) {
            duplicatedInvoiceNumbers[invoiceItem.invoice.invoice_no] = true
          }

          if (invoiceItem.invoice.status === 'credited') {
            creditedInvoiceNos[invoiceItem.invoice.invoice_no] = true
          } else if (invoiceItem.invoice.status === 'paid') {
            paidInvoiceNos[invoiceItem.invoice.invoice_no] = true
          } else if (invoiceItem.invoice.status === 'cancelled') {
            cancelledInvoiceNos[invoiceItem.invoice.invoice_no] = true
          }

          if (parseFloat(invoiceItem.amount) > parseFloat(invoiceItem.invoice.outstanding_amount)) {
            exceedingTargetAmountInvoiceNos[invoiceItem.invoice.invoice_no] = true
          }

          invoiceUuids[invoiceItem.invoice_uuid] = true
          totalAmount += parseFloat(invoiceItem.amount)
        }

        if (Object.keys(duplicatedInvoiceNumbers).length > 0) {
          this.$errorDialog(this.$i18n.t('Some of the invoices were added multiple times and are duplicated. Please, remove duplicated invoices and try again. Invoices: {invoice_nos}', {invoice_nos: Object.keys(duplicatedInvoiceNumbers).join(', ')}))
          return
        }

        if (totalAmount > overpaymentAmount) {
          this.$errorDialog(this.$i18n.t('Sum of allocated amounts cannot exceed overpaid amount. Please, change amount field(s) and try again.'))
          return false
        }

        if (Object.keys(creditedInvoiceNos).length > 0) {
          this.$errorDialog(this.$i18n.t('Some of the target invoices are credited. Payment allocation to credited invoices is not allowed. Please, remove credited invoices and try again. Invoices: {invoice_nos}', {invoice_nos: Object.keys(creditedInvoiceNos).join(', ')}))
          return
        }

        if (Object.keys(cancelledInvoiceNos).length > 0) {
          this.$errorDialog(this.$i18n.t('Some of the target invoices are cancelled. Payment allocation to cancelled invoices is not allowed. Please, remove cancelled invoices and try again. Invoices: {invoice_nos}', {invoice_nos: Object.keys(cancelledInvoiceNos).join(', ')}))
          return
        }

        if (Object.keys(paidInvoiceNos).length > 0) {
          let confirm = await this.$confirm(this.$i18n.t('Some of the target invoice(s) are already paid. Are you sure you would like to proceed and allocate the overpayment to following invoice(s)? Invoices: {invoice_nos}', {invoice_nos: Object.keys(paidInvoiceNos).join(', ')}))
          if (!confirm) {
            return false
          }
        }

        if (Object.keys(exceedingTargetAmountInvoiceNos).length > 0) {
          let confirm = await this.$confirm(this.$i18n.t('Some of the target invoice(s) exceed the outstanding amount. Are you sure you would like to proceed and allocate the overpayment to following invoice(s)? Invoices: {invoice_nos}', {invoice_nos: Object.keys(exceedingTargetAmountInvoiceNos).join(', ')}))
          if (!confirm) {
            return false
          }
        }

        let requestData = this.model.invoices.map(i => {
          return {
            amount: parseFloat(i.amount),
            target_invoice_uuid: i.invoice_uuid,
            source_invoice_uuid: this.sourceInvoice.uuid,
            date: this.model.date,
            bank_account: this.model.bank_account
          }
        })

        let response = await this.submitAllocatePayment(requestData)

        if (response.success) {
          this.$toast('success', this.$i18n.t('Payment(s) were successfully allocated'))
          this.onHidden()
          this.getTableData()
        } else {
          if (response.body.non_field_errors) {
            const listItemsArr = [];
            response.body.non_field_errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Allocate Payment Error'));
          }
        }
      },
      onAddInvoiceClick() {
        this.model.invoices.push(new AllocatePaymentItemModel({uuid: uuid.v4()}))
      },
      onInvoiceItemRemoveClick(item) {
        let index = this.model.invoices.findIndex(i => i.uuid === item.uuid)
        if (index > -1) {
          this.model.invoices.splice(index, 1)
        }
      },
      onInvoiceSelect(data, invoiceItem) {
        invoiceItem.invoice = data
        invoiceItem.outstanding_amount = data.outstanding_amount
      },
      onInvoiceUnselect(invoiceItem) {
        invoiceItem.invoice = null
        invoiceItem.invoice_uuid = null
        invoiceItem.outstanding_amount = null
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "./../scss/variables";

</style>
